import { datadogRum } from "@datadog/browser-rum";
import {
  getProfileExperiences,
  getUserProfile,
  HttpError,
  sendEmailVerificationCodeLegacy,
  sendEmailVerificationCodeStart,
  sendPhoneVerificationCodeLegacy,
  sendPhoneVerificationCodeStart,
  updateEmailSubscription,
  updateProfileDetails,
  UpdateProfileDetailsBody,
  updateProfileLegacy,
  verifyEmailComplete,
  verifyEmailLegacy,
  verifyPhoneComplete,
  verifyPhoneLegacy,
} from "@peerspace/bellhop";
import { setCookie } from "cookies-next";
import parsePhoneNumber from "libphonenumber-js";

import { handleLogout } from "~/api/auth-service";
import { getQueryClient } from "~/lib/react-query/query-client";
import type { IProfile, UpdateProfileResponse } from "~/types/api-types";
import type { NormalizedProfile, ProfileData } from "~/types/profile.types";
import { TERMS_UPDATE_REDIRECT_COOKIE } from "~/utils/constants";
import { createCookieOptions } from "~/utils/cookie-utils";
import { snakeToCamel } from "~/utils/snakeToCamel";

import { profileKeys } from "./profile.keys";

/** Response from the send-phone-verification-code endpoint. */
type PhoneVerificationSendResponse =
  { verification_id: string; code?: string } | { message: string };

/** Response from the send-email-verification-code endpoint. */
type EmailVerificationSendResponse =
  { verification_id: string; code?: string } | { message: string };

/**
 * queryFn for the `profile` query: fetches the logged-in user's profile and
 * normalizes it to camelCase. Stays pure — the query owns caching, so this does
 * not write to the cache itself.
 *
 * Resolves to `null` (never `undefined`) when there is no profile: v5 rejects an
 * `undefined` query result, which would put the query in an error state instead
 * of the logged-out state we want. `null` is the same logged-out value
 * `logOutUser` writes to the cache.
 */
const fetchProfile = async (): Promise<NormalizedProfile | null> => {
  try {
    const profile = await getUserProfile();
    return snakeToCamel(profile);
  } catch (error) {
    // Bellhop throws HttpError({ kind: "auth" }) when no PSAccess cookie is
    // present or 401/403 from the backend means the session is no longer valid — drop
    // to a logged-out state instead of leaving the query in a retrying error
    // state. Bellhop surfaces backend 401s as HttpError kind "http" status 401.
    if (
      error instanceof HttpError &&
      (error.is("auth") || error.status === 401 || error.status === 403)
    ) {
      await logOutUser();
      return null;
    }

    // we throw this error because the only errors we should get back from the api
    // just in case we want to know what's going on, but not log out the user to investigate
    datadogRum.addError(error, { source: "fetchProfile" });
    throw error;
  }
};

/**
 * queryFn for the `experiences` query: fetches the user's pending "terms"
 * experiences (e.g. an updated-terms acceptance).
 */
const fetchProfileExperiences = () => getProfileExperiences({ name: "terms" });

/**
 * Fetches the pending experiences (through the query cache) and, if the user has
 * a pending updated-terms experience, stashes the current URL in `turls` and
 * redirects the browser to `/updated-terms`.
 *
 * Returns `true` when the caller should stop its own post-auth flow — i.e. a
 * redirect was triggered or the user was logged out — and `false` when there was
 * nothing pending and the caller may proceed.
 */
const redirectToUpdatedTermsIfPending = async (): Promise<boolean> => {
  // Avoid reloading if already on the terms page
  if (location.pathname.endsWith("/updated-terms")) {
    return true;
  }

  try {
    const experiences = await getQueryClient().query({
      queryFn: fetchProfileExperiences,
      queryKey: profileKeys.experiences,
    });

    if ((experiences?.data?.length ?? 0) > 0) {
      setCookie(
        TERMS_UPDATE_REDIRECT_COOKIE,
        `${location.pathname}${location.search}${location.hash}`,
        createCookieOptions({
          expires: new Date(Date.now() + 60 * 60 * 6 * 1000),
        }),
      );

      // client-side can't evaluate this with Nextjs functions - this is the dependable workaround
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      location.href = "/updated-terms";
      return true;
    }

    return false;
  } catch {
    // we aren't redirecting the user, so just let them continue to the next step
    return false;
  }
};

// Used in the short term - will be removed 30 days after FA sign up is live to prod
// TODO: remove this after Nov 12th 2026
const getIsUserFAEnabled = (): boolean =>
  Boolean(
    getQueryClient().getQueryData<NormalizedProfile>(profileKeys.profile)
      ?.extUserId,
  );

/**
 * Merges a partial, API-shaped (snake_case) profile patch into the cached
 * profile — the cache-writing equivalent of the old context's `setProfileData`.
 */
const patchProfileCache = (patch: Partial<IProfile>): void => {
  getQueryClient().setQueryData<NormalizedProfile>(
    profileKeys.profile,
    (previous) => {
      // if BE doesn't send the phone_formatted back but does the phone_e164 we have to format it
      if (patch?.phone_e164 && !patch?.phone_formatted) {
        patch.phone_formatted = parsePhoneNumber(
          patch.phone_e164,
        )?.formatNational();
      }
      return { ...previous, ...snakeToCamel(patch) } as NormalizedProfile;
    },
  );
};

/**
 * Updates the profile via the API and returns the server response. Pure — it
 * performs no cache writes. The calling mutation's `onSuccess` owns patching the
 * cache (see `profileOptions.mutations.update` in `useProfile.ts`), which keeps
 * a single write path so every profile update flows through one mutation.
 */
const updateProfileData = ({
  path,
  payload,
}: {
  path?: string;
  payload: Partial<IProfile> | UpdateProfileDetailsBody;
}): Promise<UpdateProfileResponse> => {
  const shouldUseNewPath = getIsUserFAEnabled();
  return shouldUseNewPath
    ? (updateProfileDetails(
        payload as UpdateProfileDetailsBody,
      ) as unknown as Promise<UpdateProfileResponse>)
    : (updateProfileLegacy(
        { path: path ?? "me" },
        payload as Partial<IProfile>,
      ) as unknown as Promise<UpdateProfileResponse>);
};

const updateEmailSubscriptionService = async (subscribed: boolean) => {
  const shouldUseNewPath = getIsUserFAEnabled();
  if (shouldUseNewPath) {
    const newStyleData = await updateEmailSubscription({ subscribed });
    return newStyleData.subscribed;
  } else {
    const oldStyleData = (await updateProfileLegacy(
      { path: "me" },
      { email_unsubscribes: { all: !subscribed } },
    )) as unknown as UpdateProfileResponse;
    return !oldStyleData?.profile.email_unsubscribes?.all;
  }
};

/**
 * Verifies the user's email with the supplied code. The `verification_id` is
 * read from the cache — it's written there by the send-code mutation's
 * `onSuccess` (see `profileOptions.mutations.sendEmailVerificationCode`), so the
 * caller only needs to pass the code the user typed.
 *
 * Pure otherwise — the `emailVerified` cache flip lives in the verify mutation's
 * `onSuccess` (see `profileOptions.mutations.verifyEmail`).
 */
/**
 */
const verifyProfileEmail = async ({
  verificationCode,
}: {
  verificationCode: string;
}): Promise<{ message: string }> => {
  const shouldUseNewPath = getIsUserFAEnabled();
  if (shouldUseNewPath) {
    const sendResponse =
      getQueryClient().getQueryData<EmailVerificationSendResponse>(
        profileKeys.emailVerification,
      );
    return (await verifyEmailComplete({
      code: verificationCode,
      verification_id:
        sendResponse && "verification_id" in sendResponse
          ? sendResponse.verification_id
          : undefined,
    })) as { message: string };
  }
  return (await verifyEmailLegacy({
    email_verification_code: verificationCode,
  })) as { message: string };
};

/**
 * Verifies the user's phone with the supplied code. The `verification_id` is
 * read from the cache — it's written there by the send-code mutation's
 * `onSuccess` (see `profileOptions.mutations.sendPhoneVerificationCode`), so the
 * caller only needs to pass the code the user typed.
 */
const verifyProfilePhone = async ({
  verificationCode,
}: {
  verificationCode: string;
}): Promise<{ message: string }> => {
  const shouldUseNewPath = getIsUserFAEnabled();
  if (shouldUseNewPath) {
    const sendResponse =
      getQueryClient().getQueryData<PhoneVerificationSendResponse>(
        profileKeys.phoneVerification,
      );
    return (await verifyPhoneComplete({
      code: verificationCode,
      verification_id:
        sendResponse && "verification_id" in sendResponse
          ? sendResponse.verification_id
          : undefined,
    })) as { message: string };
  }
  return (await verifyPhoneLegacy({
    phone_verification_code: verificationCode,
  })) as { message: string };
};

/**
 * Sends a phone verification code to the user's phone and returns the API
 * response. Pure — no cache writes; the send-code mutation's `onSuccess` stashes
 * this response so `verifyProfilePhone` can read the `verification_id` back out.
 */
const requestPhoneVerificationCode =
  async (): Promise<PhoneVerificationSendResponse> => {
    const shouldUseNewPath = getIsUserFAEnabled();
    const response = shouldUseNewPath
      ? await sendPhoneVerificationCodeStart()
      : await sendPhoneVerificationCodeLegacy();
    return response as PhoneVerificationSendResponse;
  };

/**
 * Sends an email verification code to the user's email and returns the API
 * response. Pure — no cache writes; the query owns the response so
 * `verifyProfileEmail` can read the `verification_id` back out.
 */
const requestEmailVerificationCode =
  async (): Promise<EmailVerificationSendResponse> => {
    const shouldUseNewPath = getIsUserFAEnabled();
    const response = shouldUseNewPath
      ? await sendEmailVerificationCodeStart()
      : await sendEmailVerificationCodeLegacy();
    return response as EmailVerificationSendResponse;
  };

/**
 * Logs the user out (best-effort) and clears the cached profile so the app drops
 * to a logged-out state. The reset runs even if the logout request fails; the
 * error still propagates for callers that want to surface it.
 */
const logOutUser = async (): Promise<void> => {
  const queryClient = getQueryClient();
  try {
    await handleLogout();
  } finally {
    await queryClient.cancelQueries({ queryKey: profileKeys.profile });
    // we have to use `setQueryData` to null (not `undefined`, which v5 ignores)
    // so it will overwrite the cache with a logged-out value
    // AND marks it fresh, so mounted `useProfile()` observers
    // don't immediately refetch the profile back in.
    queryClient.setQueryData(profileKeys.profile, null);
  }
};

/**
 * Loads the profile into the cache after authentication (e.g. Google One Tap),
 * forcing a fresh fetch even if a logged-out `null` is still cached. Mirrors the
 * old context's `loadInitialProfile`: resolves the profile and logs the user out
 * if the load fails.
 */
const loadInitialProfile = async (): Promise<ProfileData> => {
  try {
    const data = await getQueryClient().query({
      queryFn: fetchProfile,
      queryKey: profileKeys.profile,
      staleTime: 0,
    });
    return data;
  } catch {
    await logOutUser();
  }
};

/**
 * Invalidates the profile query, triggering a background refetch — the explicit
 * cache invalidation the old Context had no concept of.
 */
const refreshProfile = async (): Promise<void> => {
  await getQueryClient().invalidateQueries({ queryKey: profileKeys.profile });
};

export {
  fetchProfile,
  fetchProfileExperiences,
  loadInitialProfile,
  logOutUser,
  patchProfileCache,
  redirectToUpdatedTermsIfPending,
  refreshProfile,
  requestEmailVerificationCode,
  requestPhoneVerificationCode,
  updateEmailSubscriptionService,
  updateProfileData,
  verifyProfileEmail,
  verifyProfilePhone,
};
